import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useConfigurationClinicImagesAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationClinicImagesAPI";
import { useConfigurationClinicImagesUpdateAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationClinicImagesUpdateAPI";
import { buildClinicImagesPayload, hydrateImageGroup, useImageValidator } from "@/app/hooks/custom_hooks";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import { useRouter } from "@/app/hooks/useRouter";
import { ArrowUpOnSquareStackIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router";

interface IClinicImagesSetupProps {
  // Passed when embedded in ClinicOnboard (step 2). When omitted, the component runs
  // standalone as the clinic-image-update route: it fetches, hydrates and saves itself.
  formHook?: any;
  listcategories?: Record<string, string[]>;
  setListcategories?: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  detailcategories?: Record<string, string[]>;
  setDetailcategories?: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  isSubmit?: boolean;
  headerAction?: React.ReactNode;
  topSection?: React.ReactNode;
}

const RemoveImageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
    <path
      d="M14.25 5.3075L13.1925 4.25L9 8.4425L4.8075 4.25L3.75 5.3075L7.9425 9.5L3.75 13.6925L4.8075 14.75L9 10.5575L13.1925 14.75L14.25 13.6925L10.0575 9.5L14.25 5.3075Z"
      fill="black"
    />
  </svg>
);

// const defaultCategories = ["FACADE", "RECEPTION", "GROOMING", "MEDICAL", "RETAIL"];
const defaultCategories = ["ENTRANCE", "GROOMING", "MEDICAL", "HAPPY PETS"];

// API may return each category as a plain array of URLs or as {base_64: [...], image_extension}
const normalizePage = (page: any) => {
  if (!page) return null;
  const normalized = {};
  for (const category in page) {
    const value = page[category];
    normalized[category] = Array.isArray(value) ? value : value?.base_64 || [];
  }
  return normalized;
};

const ClinicImagesSetup = (props: IClinicImagesSetupProps) => {
  let imagesFetched = false;
  const isStandalone = !props.formHook;
  const { isSubmit = false, headerAction, topSection } = props;
  const params = useParams();
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const localFormHook = useForm();
  const [localListcategories, setLocalListcategories] = useState<Record<string, string[]>>({});
  const [localDetailcategories, setLocalDetailcategories] = useState<Record<string, string[]>>({});
  const formHook = props.formHook ?? localFormHook;
  const listcategories = props.listcategories ?? localListcategories;
  const setListcategories = props.setListcategories ?? setLocalListcategories;
  const detailcategories = props.detailcategories ?? localDetailcategories;
  const setDetailcategories = props.setDetailcategories ?? setLocalDetailcategories;
  const { setVisible: imageCategoryModal, setModalData: setImgCategory } = useModalActions("IMAGE_CATEGORY_MODAL");
  const addedImgCategory = useModalState("IMAGE_CATEGORY_MODAL");
  const { validateImage } = useImageValidator();
  const { clinicImages, isFetching, execute: fetchClinicImages } = useConfigurationClinicImagesAPI();
  const { updatedImagesClinicId, isUpdating, execute: updateClinicImages } = useConfigurationClinicImagesUpdateAPI();

  useEffect(() => {
    if (isStandalone && params["clinic_id"] && !imagesFetched) {
      imagesFetched = true;
      fetchClinicImages(params["clinic_id"]);
    }
  }, [params["clinic_id"]]);

  useEffect(() => {
    if (clinicImages) {
      const image = clinicImages?.image_version?.image_base64 || clinicImages?.image_url;
      if (image) {
        formHook.setValue("image", image.split(",")[0]);
      }
      hydrateImageGroup(normalizePage(clinicImages?.clinic_listing_page), "clinic_listing_page", formHook, setListcategories);
      hydrateImageGroup(normalizePage(clinicImages?.clinic_details_page), "clinic_details_page", formHook, setDetailcategories);
    }
  }, [clinicImages]);

  useEffect(() => {
    if (updatedImagesClinicId) {
      navigate(getRouteKey("CLINIC_SETUP", "url"));
    }
  }, [updatedImagesClinicId]);

  const handleSave = () => {
    updateClinicImages(buildClinicImagesPayload(formHook, listcategories, detailcategories), params["clinic_id"]);
  };

  useEffect(() => {
    if (addedImgCategory != "") {
      setListcategories((prev) => ({ ...prev, [addedImgCategory]: [] }));
      setDetailcategories((prev) => ({ ...prev, [addedImgCategory]: [] }));
    }
  }, [addedImgCategory]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, key, category) => {
    const files = event.target.files;
    const images: string[] = [];
    if (!files) return;
    for (const file of Array.from(files)) {
      if (file) {
        const result = await validateImage(file, key);
        if (result.isValid) {
          images.push(result.imgSrc);
        } else {
          if (!result.isPng) {
            toast.error("Please upload a .png file.");
          } else if (!result.isSizeValid) {
            toast.error("Please upload an image less than 50KB.");
          } else if (!result.isDimensionsValid) {
            toast.error(
              `Please upload an image with dimensions ${key == "clinic_details_page" || key == "details_category" ? "360x200" : "328x160"} pixels.`,
            );
          }
        }
      }
    }
    if (key == "image") {
      formHook.setValue("image", images[0]);
    } else if (key == "listing_category") {
      setListcategories((prev) => ({ ...prev, [category]: [...(prev[category] || []), ...images] }));
    } else if (key == "details_category") {
      setDetailcategories((prev) => ({ ...prev, [category]: [...(prev[category] || []), ...images] }));
    } else {
      let prev = formHook.watch(key);
      if (prev && category in prev) {
        prev = prev[category];
      } else {
        prev = [];
      }
      formHook.setValue(key, { ...formHook.watch(key), [category]: [...prev, ...images] });
    }
  };

  const handleRemoveImage = (key, category, i) => {
    if (key == "image") {
      formHook.setValue("image", "");
    } else if (key == "listing_category") {
      setListcategories((prev) => {
        const newListCategories = { ...prev };
        if (newListCategories[category].length == 1) {
          delete newListCategories[category];
        } else {
          newListCategories[category] = newListCategories[category]?.filter((_, j) => j != i);
        }
        return newListCategories;
      });
    } else if (key == "details_category") {
      setDetailcategories((prev) => {
        const newDetailCategories = { ...prev };
        if (newDetailCategories[category]?.length == 1) {
          delete newDetailCategories[category];
        } else {
          newDetailCategories[category] = newDetailCategories[category]?.filter((_, j) => j != i);
        }
        return newDetailCategories;
      });
    } else {
      const prev = formHook.watch(key);
      if (prev[category]?.length == 1) {
        const { [category]: deletedcategory, ...rest } = prev;
        formHook.setValue(key, rest);
      } else {
        formHook.setValue(key, {
          ...prev,
          [category]: prev[category]?.filter((_, j) => j != i),
        });
      }
    }
  };

  const handleRemoveAll = (key, category) => {
    if (key == "listing_category") {
      setListcategories((prev) => {
        const newListCategories = { ...prev };
        newListCategories[category] = [];
        return newListCategories;
      });
    } else if (key == "details_category") {
      setDetailcategories((prev) => {
        const newListCategories = { ...prev };
        newListCategories[category] = [];
        return newListCategories;
      });
    } else {
      const { [category]: deletedcategory, ...rest } = formHook.watch(key);
      formHook.setValue(key, rest);
    }
  };

  const handleDeleteCategory = (key, category: string) => {
    setImgCategory("");
    if (key === "listing_category") {
      setListcategories((prev: { [key: string]: string[] }) => {
        const { [category]: deletedCategory, ...newList } = prev;
        return newList;
      });
    }
    if (key === "details_category") {
      setDetailcategories((prev) => {
        const newDetailCategories = { ...prev };
        delete newDetailCategories[category];
        return newDetailCategories;
      });
    }
  };

  const renderImagesSetup = () => (
    <>
      <div className="px-3 py-[9px] w-full bg-white rounded-xl shadow">
        <div className="px-3 justify-between items-center flex">
          <div className="text-black text-base font-semibold">Clinic Images Setup</div>
          {headerAction}
        </div>
      </div>
      {topSection}
      <div className="mt-5 py-2 bg-white flex-col gap-1.5 flex">
        <div className="pl-3 py-[7px] border-b border-slate-400 border-opacity-20 justify-start items-center inline-flex">
          <div className="justify-start items-center gap-9 flex">
            <div className="text-black text-sm font-medium">Enter Non-Category Images</div>
          </div>
        </div>
      </div>
      <div className="justify-start items-start gap-10 inline-flex">
        <div className="p-2.5 justify-start items-start gap-2.5 flex">
          <div>
            <span className="text-black text-xs font-normal">
              Image version 1<br />
            </span>
            <span className="text-slate-400 text-[10px] font-medium">(.png format, 328 Px X 160 Px and 50 KB)</span>
          </div>
        </div>
        {!formHook.watch("image") && (
          <div className="py-5 flex-col justify-start items-start gap-10 inline-flex">
            <label htmlFor="imageUpload" className="pr-[26px] cursor-pointer items-center gap-[3.73px] flex">
              <div className="text-blue-600">
                <ArrowUpOnSquareStackIcon className="w-4 h-4" />
              </div>
              <input
                type="file"
                id="imageUpload"
                name="image"
                accept=".png"
                onChange={(e) => handleImageUpload(e, "image", "")}
                className="hidden"
              />
              <div className="text-center text-blue-600 text-xs font-medium">Upload</div>
            </label>
          </div>
        )}
        {(formHook.watch("image") || false) && (
          <div className="mt-3 relative">
            <img src={`${formHook.watch("image")}`} className="w-32 h-32" />
            <button
              className="absolute top-0 bg-transparent border-none text-black text-para-fontsize cursor-pointer"
              onClick={(event) => {
                event.preventDefault();
                handleRemoveImage("image", "", 0);
              }}
            >
              <RemoveImageIcon />
            </button>
          </div>
        )}
      </div>
      <div className="pl-3 mt-10 py-[7px] justify-between border-b border-slate-400 border-opacity-20 flex">
        <div className="items-center flex">
          <div className="text-black text-sm font-medium">Enter Category Images</div>
        </div>
        <div
          onClick={() => {
            imageCategoryModal();
          }}
          className="px-4 cursor-pointer py-2 bg-gray-100 rounded-md"
        >
          <div className="text-black text-xs font-semibold">+ Add New Category</div>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="py-4 justify-start items-start gap-10 inline-flex">
          <div className="p-2.5 justify-start items-start gap-2.5 flex">
            <div className="">
              <span className="text-black text-xs font-normal">
                Clinic Listing Page
                <br />
              </span>
              <span className="text-slate-400 text-[10px] font-medium">(.png format, 328px x 160px and 50 KB)</span>
            </div>
          </div>
          <div className="py-2.5 flex-col justify-start items-start gap-10 inline-flex">
            {defaultCategories.map((category, i) => (
              <div key={i}>
                <div className="justify-start items-center gap-8 inline-flex">
                  <div className="w-[100px] justify-start items-start gap-1 flex">
                    <div>
                      <span className="text-black text-xs font-medium">{category}</span>
                      <span className="text-rose-600 text-xs font-medium">*</span>
                    </div>
                  </div>
                  <div className="flex-col justify-start items-start gap-10 inline-flex">
                    <label
                      htmlFor={`clinic_listing_page-${category}`}
                      className="pr-[26px] cursor-pointer items-center gap-[3.73px] flex"
                    >
                      <div className="text-blue-600">
                        <ArrowUpOnSquareStackIcon className="w-4 h-4" />
                      </div>
                      <input
                        type="file"
                        id={`clinic_listing_page-${category}`}
                        name={`clinic_listing_page-${category}`}
                        accept=".png"
                        onChange={(e) => {
                          handleImageUpload(e, "clinic_listing_page", category);
                        }}
                        className="hidden"
                        multiple
                      />
                      <div className="text-center text-blue-600 text-xs font-medium">Upload</div>
                    </label>
                  </div>
                  {formHook.watch("clinic_listing_page")?.[category]?.length > 1 && (
                    <div className="flex-col justify-start items-start gap-10 inline-flex">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          handleRemoveAll("clinic_listing_page", category);
                        }}
                      >
                        <div className="text-center text-blue-600 text-xs font-medium">Remove All</div>
                      </button>
                    </div>
                  )}
                  {isSubmit &&
                    (formHook.watch("clinic_listing_page")?.[category] == undefined ||
                      formHook.watch("clinic_listing_page")?.[category]?.length == 0) && (
                      <div className="text-[9px]  mt-1 text-red-500">{category} Image is required</div>
                    )}
                </div>
                {(formHook.watch("clinic_listing_page")?.[category] || false) && (
                  <div className="flex gap-3 overflow-scroll">
                    {formHook.watch("clinic_listing_page")?.[category]?.map((img, i) => (
                      <div className="mt-3 relative" key={i}>
                        <img src={img} alt="Selected Image" className="w-32 h-32" />
                        <button
                          className="absolute top-0 bg-transparent border-none text-black text-para-fontsize cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            handleRemoveImage("clinic_listing_page", category, i);
                          }}
                        >
                          <RemoveImageIcon />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {Object.entries(listcategories).length > 0 &&
              Object.entries(listcategories).map(([key, imgs], i) => {
                const images = imgs as string[];
                return (
                  <div key={i}>
                    <div className="justify-start items-center gap-8 inline-flex">
                      <div className="w-[100px] justify-start items-start gap-1 flex">
                        <div>
                          <span className="text-black text-xs font-medium">{key}</span>
                          <span className="text-rose-600 text-xs font-medium">*</span>
                        </div>
                      </div>
                      <div className="flex-col justify-start items-start gap-10 inline-flex">
                        <label
                          htmlFor={`listing_category-${key}`}
                          className="pr-[26px] cursor-pointer items-center gap-[3.73px] flex"
                        >
                          <div className="text-blue-600">
                            <ArrowUpOnSquareStackIcon className="w-4 h-4" />
                          </div>
                          <input
                            type="file"
                            id={`listing_category-${key}`}
                            name={`listing_category-${key}`}
                            accept=".png"
                            onChange={(e) => {
                              handleImageUpload(e, "listing_category", key);
                            }}
                            className="hidden"
                            multiple
                          />
                          <div className="text-center text-blue-600 text-xs font-medium">Upload</div>
                        </label>
                      </div>
                      {images?.length > 1 && (
                        <div className="flex-col justify-start items-start gap-10 inline-flex">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleRemoveAll("listing_category", key);
                            }}
                          >
                            <div className="text-center text-blue-600 text-xs font-medium">Remove All</div>
                          </button>
                        </div>
                      )}
                      <div className="flex-col justify-start items-start gap-10 inline-flex">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handleDeleteCategory("listing_category", key);
                          }}
                        >
                          <div className="text-center text-blue-600 text-xs font-medium">Delete Category</div>
                        </button>
                      </div>
                      {isSubmit && (listcategories?.[key] == undefined || listcategories?.[key]?.length == 0) && (
                        <div className="text-[9px]  mt-1 text-red-500">{key} Image is required</div>
                      )}
                    </div>
                    <div className="flex gap-3 overflow-scroll">
                      {images?.length > 0 &&
                        images?.map((img, i) => (
                          <div className="mt-3 relative" key={i}>
                            <img src={img} alt="Selected Image" className="w-32 h-32" />
                            <button
                              className="absolute top-0 bg-transparent border-none text-black text-para-fontsize cursor-pointer"
                              onClick={(e) => {
                                e.preventDefault();
                                handleRemoveImage("listing_category", key, i);
                              }}
                            >
                              <RemoveImageIcon />
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        <div className="py-4 mb-20 justify-start items-start gap-10 inline-flex">
          <div className="p-2.5 justify-start items-start gap-2.5 inline-flex">
            <div className="">
              <span className="text-black text-xs font-normal">
                Clinic Details Page
                <br />
              </span>
              <span className="text-slate-400 text-[10px] font-medium">(.png format, 360px x 200px and 50 KB)</span>
            </div>
          </div>
          <div className="py-2.5 flex-col justify-start items-start gap-10 inline-flex">
            {defaultCategories.map((category, i) => (
              <div key={i}>
                <div className="justify-start items-center gap-8 inline-flex">
                  <div className="w-[100px] justify-start items-start gap-1 flex">
                    <div>
                      <span className="text-black text-xs font-medium">{category}</span>
                      <span className="text-rose-600 text-xs font-medium">*</span>
                    </div>
                  </div>
                  <div className="flex-col justify-start items-start gap-10 inline-flex">
                    <label
                      htmlFor={`clinic_details_page-${category}`}
                      className="pr-[26px] cursor-pointer items-center gap-[3.73px] flex"
                    >
                      <div className="text-blue-600">
                        <ArrowUpOnSquareStackIcon className="w-4 h-4" />
                      </div>
                      <input
                        type="file"
                        id={`clinic_details_page-${category}`}
                        name={`clinic_details_page-${category}`}
                        accept=".png"
                        onChange={(e) => {
                          handleImageUpload(e, "clinic_details_page", category);
                        }}
                        className="hidden"
                        multiple
                      />
                      <div className="text-center text-blue-600 text-xs font-medium">Upload</div>
                    </label>
                  </div>
                  {formHook.watch("clinic_details_page")?.[category]?.length > 1 && (
                    <div className="flex-col justify-start items-start gap-10 inline-flex">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          handleRemoveAll("clinic_details_page", category);
                        }}
                      >
                        <div className="text-center text-blue-600 text-xs font-medium">Remove All</div>
                      </button>
                    </div>
                  )}
                  {isSubmit &&
                    (formHook.watch("clinic_details_page")?.[category] == undefined ||
                      formHook.watch("clinic_details_page")?.[category]?.length == 0) && (
                      <div className="text-[9px]  mt-1 text-red-500">{category} Image is required</div>
                    )}
                </div>
                {(formHook.watch("clinic_details_page")?.[category] || false) && (
                  <div className="flex gap-2 overflow-scroll">
                    {formHook.watch("clinic_details_page")?.[category]?.map((img, i) => (
                      <div className="mt-3 relative" key={i}>
                        <img src={img} alt="Selected Image" className="w-32 h-32" />
                        <button
                          className="absolute top-0 bg-transparent border-none text-black text-para-fontsize cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            handleRemoveImage("clinic_details_page", category, i);
                          }}
                        >
                          <RemoveImageIcon />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {Object.entries(detailcategories).length > 0 &&
              Object.entries(detailcategories).map(([key, imgs], i) => {
                const images = imgs as string[];
                return (
                  <div key={i}>
                    <div className="justify-start items-center gap-8 inline-flex">
                      <div className="w-[100px] justify-start items-start gap-1 flex">
                        <div>
                          <span className="text-black text-xs font-medium">{key}</span>
                          <span className="text-rose-600 text-xs font-medium">*</span>
                        </div>
                      </div>
                      <div className="flex-col justify-start items-start gap-10 inline-flex">
                        <label
                          htmlFor={`details_category-${key}`}
                          className="pr-[26px] cursor-pointer items-center gap-[3.73px] flex"
                        >
                          <div className="text-blue-600">
                            <ArrowUpOnSquareStackIcon className="w-4 h-4" />
                          </div>
                          <input
                            type="file"
                            id={`details_category-${key}`}
                            name={`details_category-${key}`}
                            accept=".png"
                            onChange={(e) => {
                              handleImageUpload(e, "details_category", key);
                            }}
                            className="hidden"
                            multiple
                          />
                          <div className="text-center text-blue-600 text-xs font-medium">Upload</div>
                        </label>
                      </div>
                      {images?.length > 1 && (
                        <div className="flex-col justify-start items-start gap-10 inline-flex">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleRemoveAll("details_category", key);
                            }}
                          >
                            <div className="text-center text-blue-600 text-xs font-medium">Remove All</div>
                          </button>
                        </div>
                      )}
                      <div className="flex-col justify-start items-start gap-10 inline-flex">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handleDeleteCategory("details_category", key);
                          }}
                        >
                          <div className="text-center text-blue-600 text-xs font-medium">Delete Category</div>
                        </button>
                      </div>
                      {isSubmit && (detailcategories?.[key] == undefined || detailcategories?.[key]?.length == 0) && (
                        <div className="text-[9px]  mt-1 text-red-500">{key} Image is required</div>
                      )}
                    </div>
                    <div className="flex gap-2 overflow-scroll">
                      {images?.length > 0 &&
                        images.map((img, i) => (
                          <div className="mt-3 relative" key={i}>
                            <img src={img} alt="Selected Image" className="w-32 h-32" />
                            <button
                              className="absolute top-0 bg-transparent border-none text-black text-para-fontsize cursor-pointer"
                              onClick={(e) => {
                                e.preventDefault();
                                handleRemoveImage("details_category", key, i);
                              }}
                            >
                              <RemoveImageIcon />
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </>
  );

  if (!isStandalone) {
    return renderImagesSetup();
  }

  return (
    <div className="mx-3 bg-white rounded-md py-3">
      {isFetching ? (
        <div className="mt-8">
          <SkeletonType1 />
          <SkeletonType1 className="mt-4" />
        </div>
      ) : (
        <>
          {renderImagesSetup()}
          <div className="justify-end items-center gap-4 inline-flex sticky bottom-0 right-0 p-3 w-full bg-white border-t-2">
            <div
              className="h-9 px-10 py-[9px] cursor-pointer bg-slate-400 bg-opacity-10 rounded-md border"
              onClick={() => navigate(getRouteKey("CLINIC_SETUP", "url"))}
            >
              <div className="text-black text-xs font-medium">Cancel</div>
            </div>
            <SolidButton asType="button" size="2XL" onButtonClick={handleSave} disabled={isUpdating}>
              {isUpdating ? "Saving..." : "Save"}
            </SolidButton>
          </div>
        </>
      )}
    </div>
  );
};

export default ClinicImagesSetup;
